import { loadProfile } from '@/embed/cache'
import {
  ItemNode,
  PassiveNode,
  ProfileItemNode,
  SkillNode
} from '@/embed/nodes'
import { decodeTooltipOptions } from '@/logic/item'
import Store from '@/store'
import { ItemTooltip } from '@/tooltip'
import Resizable from '@/ui/resizable'
import { OverlayContext } from '@/ui/utils/OverlayManager'
import { Shortcode, parseShortcodes } from '@/utils/shortcode-parser'
import {
  faBold,
  faHeading,
  faImage,
  faItalic,
  faLink,
  faListOl,
  faListUl,
  faQuoteRight,
  faStrikethrough
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { ReactElement, ReactNode } from 'react'
import { CopyNotify, copyText } from '../copy'
import { ItemDisplay } from './Paperdoll'

import ReactMarkdown from 'react-markdown/with-html'
import gfm from 'remark-gfm'
import { Plugin } from 'unified'
import type { Node, Parent } from 'unist'

import './MarkdownEditor.scss'

type Selection = {
  start: number
  end: number
  text: string
}

export function getSelection(editor: HTMLTextAreaElement): Selection {
  if (!editor) return { start: 0, end: 0, text: '' }
  const start = editor.selectionStart
  const end = editor.selectionEnd
  return { start, end, text: editor.value.substring(start, end) }
}

export function replaceSelection(
  editor: HTMLTextAreaElement,
  selection: Selection,
  text: string,
  offsetStart?: number,
  offsetEnd?: number
) {
  if (!editor) return
  editor.value =
    editor.value.substring(0, selection.start) +
    text +
    editor.value.substring(selection.end)
  editor.setSelectionRange(
    selection.start + (offsetStart || 0),
    selection.start + (offsetEnd == null ? text.length : offsetEnd)
  )
  editor.focus()
}

export function replaceLinePrefix(
  editor: HTMLTextAreaElement,
  selection: Selection,
  match: RegExp,
  prefix: string
) {
  editor.focus()
  const pos = editor.value.lastIndexOf('\n', selection.start - 1) + 1
  const line = editor.value.substring(pos)
  const m = line.match(match)
  let delta = 0
  if (m) {
    editor.value =
      editor.value.substring(0, pos) + editor.value.substring(pos + m[0].length)
    delta = -m[0].length
  } else {
    editor.value =
      editor.value.substring(0, pos) + prefix + editor.value.substring(pos)
    delta = prefix.length
  }
  editor.setSelectionRange(selection.start + delta, selection.end + delta)
}

export type ActionHandler = (
  action: (editor: HTMLTextAreaElement) => void
) => void

type ActionButton = {
  separator?: false
  title: string
  icon: ReactElement
  action: (handler: ActionHandler) => void
  hotkey?: string
  className?: string
}
type ActionSeparator = {
  separator: true
}

const buttonList = <Key extends string>(f: {
  [k in Key]: ActionButton | ActionSeparator
}) => f

export const buttons: { [key: string]: ActionButton | ActionSeparator } =
  buttonList({
    separator: {
      separator: true
    },
    bold: {
      title: 'Bold',
      icon: <FontAwesomeIcon icon={faBold} />,
      action: (handler) =>
        handler((editor) => {
          const sel = getSelection(editor)
          replaceSelection(
            editor,
            sel,
            `**${sel.text}**`,
            2,
            2 + sel.text.length
          )
        }),
      hotkey: 'Ctrl+B'
    },
    italic: {
      title: 'Italic',
      icon: <FontAwesomeIcon icon={faItalic} />,
      action: (handler) =>
        handler((editor) => {
          const sel = getSelection(editor)
          replaceSelection(editor, sel, `*${sel.text}*`, 1, 1 + sel.text.length)
        }),
      hotkey: 'Ctrl+I'
    },
    strikethrough: {
      title: 'Strikethrough',
      icon: <FontAwesomeIcon icon={faStrikethrough} />,
      action: (handler) =>
        handler((editor) => {
          const sel = getSelection(editor)
          replaceSelection(
            editor,
            sel,
            `~~${sel.text}~~`,
            2,
            2 + sel.text.length
          )
        })
    },
    heading: {
      title: 'Heading',
      icon: <FontAwesomeIcon icon={faHeading} />,
      action: (handler) =>
        handler((editor) => {
          const sel = getSelection(editor)
          replaceLinePrefix(editor, sel, /^#+ /, '## ')
        }),
      hotkey: 'Ctrl+H'
    },
    quote: {
      title: 'Quote',
      icon: <FontAwesomeIcon icon={faQuoteRight} />,
      action: (handler) =>
        handler((editor) => {
          const sel = getSelection(editor)
          replaceLinePrefix(editor, sel, /^> /, '> ')
        })
    },
    ul: {
      title: 'List',
      icon: <FontAwesomeIcon icon={faListUl} />,
      action: (handler) =>
        handler((editor) => {
          const sel = getSelection(editor)
          replaceLinePrefix(editor, sel, /^[*-] /, '* ')
        })
    },
    ol: {
      title: 'Numbered List',
      icon: <FontAwesomeIcon icon={faListOl} />,
      action: (handler) =>
        handler((editor) => {
          const sel = getSelection(editor)
          replaceLinePrefix(editor, sel, /^\d+[.)] /, '1. ')
        })
    },
    link: {
      title: 'Link',
      icon: <FontAwesomeIcon icon={faLink} />,
      action: (handler) =>
        handler((editor) => {
          const sel = getSelection(editor)
          replaceSelection(
            editor,
            sel,
            `[${sel.text}](url)`,
            1,
            1 + sel.text.length
          )
        }),
      hotkey: 'Ctrl+K'
    },
    image: {
      title: 'Image',
      icon: <FontAwesomeIcon icon={faImage} />,
      action: (handler) =>
        handler((editor) => {
          const sel = getSelection(editor)
          replaceSelection(
            editor,
            sel,
            `![${sel.text}](url)`,
            2,
            2 + sel.text.length
          )
        })
    }
  })

export const defaultToolbar = [
  buttons.bold,
  buttons.italic,
  buttons.strikethrough,
  buttons.heading,
  buttons.separator,
  buttons.quote,
  buttons.ul,
  buttons.ol,
  buttons.separator,
  buttons.link,
  buttons.image
]

function ToolbarButton({
  params,
  handler
}: {
  params: ActionButton | ActionSeparator
  handler: ActionHandler
}) {
  const onClick = React.useCallback(
    () => !params.separator && params.action(handler),
    [params, handler]
  )
  if (params.separator) {
    return <li className="d4t-separator" />
  }
  return (
    <li className={params.className}>
      <button
        title={params.title + (params.hotkey ? ` (${params.hotkey})` : '')}
        onClick={onClick}>
        {params.icon}
      </button>
    </li>
  )
}

type HistoryAction = {
  pos: number
  removed: string
  inserted: string
}

class History {
  state: string
  actions: HistoryAction[] = []
  current = 0

  constructor(state: string) {
    this.state = state
  }

  clear() {
    this.actions.length = 0
    this.current = 0
  }

  record(text: string) {
    const prev = this.state
    this.state = text
    let len = Math.min(prev.length, text.length)
    let prefix = 0
    while (prefix < len && prev[prefix] === text[prefix]) {
      prefix += 1
    }
    if (prefix >= len) return
    len -= prefix
    let suffix = 0
    while (
      suffix < len &&
      prev[prev.length - suffix - 1] === text[text.length - suffix - 1]
    ) {
      suffix += 1
    }
    const removed = prev.substring(prefix, prev.length - suffix)
    const inserted = text.substring(prefix, text.length - suffix)

    if (this.actions.length && this.current === this.actions.length) {
      const last = this.actions[this.current - 1]
      if (
        !last.removed &&
        !removed &&
        prefix === last.pos + last.inserted.length
      ) {
        last.inserted += inserted
        return
      }
      if (!last.inserted && !inserted) {
        if (last.pos === prefix + removed.length) {
          last.pos = prefix
          last.removed = removed + last.removed
          return
        }
        if (last.pos === prefix) {
          last.removed += removed
          return
        }
      }
    }

    this.actions[this.current++] = { pos: prefix, removed, inserted }
    this.actions.length = this.current
  }

  canUndo() {
    return this.current > 0
  }
  undo(input: HTMLTextAreaElement) {
    const action = this.actions[--this.current]
    this.state =
      this.state.substring(0, action.pos) +
      action.removed +
      this.state.substring(action.pos + action.inserted.length)
    input.value = this.state
    input.setSelectionRange(
      action.pos + (action.inserted.length ? 0 : action.removed.length),
      action.pos + action.removed.length
    )
    return this.state
  }
  canRedo() {
    return this.current < this.actions.length
  }
  redo(input: HTMLTextAreaElement) {
    const action = this.actions[this.current++]
    this.state =
      this.state.substring(0, action.pos) +
      action.inserted +
      this.state.substring(action.pos + action.removed.length)
    input.value = this.state
    input.setSelectionRange(
      action.pos + (action.removed.length ? 0 : action.inserted.length),
      action.pos + action.inserted.length
    )
    return this.state
  }
}

function makeHotkey(hotkey: string) {
  const parts = hotkey.split('+')
  const key = 'Key' + parts.pop()
  const shiftKey = parts.includes('Shift')
  const ctrlKey = parts.includes('Ctrl')
  const altKey = parts.includes('Alt')
  return (e: KeyboardEvent) => {
    if (e.code !== key) return false
    if (shiftKey && !e.shiftKey) return false
    if (ctrlKey && !e.ctrlKey) return false
    if (altKey && !e.altKey) return false
    return true
  }
}

export function MarkdownEditor({
  toolbar = defaultToolbar,
  value,
  onChange
}: {
  toolbar?: (ActionButton | ActionSeparator)[]
  value: string
  onChange: (value: string) => void
}) {
  const history = React.useRef<History>()
  const editorRef = React.useRef<HTMLTextAreaElement>(null)

  React.useEffect(() => {
    history.current = new History(editorRef.current?.value || '')
    return () => (history.current = undefined)
  }, [])

  React.useEffect(() => {
    if (history.current && value !== history.current.state) {
      history.current.state = value
      history.current.clear()
    }
  }, [value])

  const onEdit = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      history.current?.record(e.target.value)
      onChange(e.target.value)
    },
    [onChange]
  )

  const onAction = React.useCallback(
    (action: (editor: HTMLTextAreaElement) => void) => {
      if (!editorRef.current) return
      action(editorRef.current)
      if (history.current) {
        history.current.record(editorRef.current.value)
        onChange(editorRef.current.value)
      }
    },
    [onChange]
  )

  const hotkeys = React.useMemo(
    () =>
      toolbar
        .filter(
          (btn): btn is ActionButton & { hotkey: string } =>
            !btn.separator && btn.hotkey != null
        )
        .map((btn) => ({ filter: makeHotkey(btn.hotkey), action: btn.action })),
    [toolbar]
  )

  React.useEffect(() => {
    // we have to use raw event listenere here because
    // 1. HistoryChart installs a global listener for Ctrl+Z on document and we need to call stopPropagation in time
    // 2. React doesn't provide `code` in synthetic events so we'd have to check legacy keyCode
    // Note: (1) only applies to D3Planner, and (2) is no longer true. But we might as well keep this behavior now
    function onKeyDown(e: KeyboardEvent) {
      for (const key of hotkeys) {
        if (key.filter(e)) {
          e.stopPropagation()
          e.preventDefault()
          key.action(onAction)
          return
        }
      }
      if (e.code === 'KeyZ' && e.ctrlKey && editorRef.current) {
        e.stopPropagation()
        e.preventDefault()
        if (e.shiftKey) {
          if (history.current?.canRedo()) {
            onChange(history.current.redo(editorRef.current))
          }
        } else {
          if (history.current?.canUndo()) {
            onChange(history.current.undo(editorRef.current))
          }
        }
      }
    }
    const node = editorRef.current
    if (node) {
      node.addEventListener('keydown', onKeyDown)
      return () => node.removeEventListener('keydown', onKeyDown)
    }
  }, [onChange, hotkeys, onAction])

  return (
    <Resizable className="poe-MdEditor" side="s">
      <ul className="poe-toolbar">
        {toolbar.map((btn, index) => (
          <ToolbarButton key={index} params={btn} handler={onAction} />
        ))}
      </ul>
      <div className="poe-editor">
        <textarea
          value={value}
          onChange={onEdit}
          ref={editorRef}
          spellCheck={false}
        />
      </div>
    </Resizable>
  )
}

function Link({
  children,
  href,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string
}) {
  const overlays = React.useContext(OverlayContext)
  const onClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (e.ctrlKey || e.altKey || e.shiftKey) return
      if (href.match(/pastebin\.com\//)) {
        e.preventDefault()
        copyText(href)
        overlays.async(<CopyNotify pos={{ left: e.pageX, top: e.pageY }} />)
      }
    },
    [href, overlays]
  )
  return (
    <a
      {...props}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}>
      {children}
    </a>
  )
}

function CustomItemNode({ id, children }: { id: number; children: ReactNode }) {
  const item = Store.useItem(id)
  if (!item) return children
  return <ProfileItemNode item={item} text={children} />
}

function LinkRenderer({
  href,
  children
}: {
  href: string
  children: ReactNode
}) {
  const parts = href.split('.')
  switch (parts[0]) {
    case 'item':
      return <ItemNode id={parts[1]} text={children} />
    case 'custom':
      return <CustomItemNode id={parseInt(parts[1])} children={children} />
    case 'passive':
      return <PassiveNode id={parts[1]} text={children} />
    case 'atlas':
      return <PassiveNode id={parts[1]} atlas text={children} />
    default:
      return (
        <Link className="poe-link" href={href}>
          {children}
        </Link>
      )
  }
}

const store = new Store()
function render(elem: ReactNode) {
  return <Store.Provider store={store}>{elem}</Store.Provider>
}

export function getShortCodeContent(shortcode: Shortcode) {
  if (!shortcode.attributes.id) return null
  const id = shortcode.attributes.id.split(' ')
  const iid = id[0]
  const content = { text: shortcode.content }
  const iconOnly = id[1] === 'icon'

  switch (shortcode.type) {
    case 'poe-item':
      const profile = shortcode.attributes.profile
      if (profile) {
        loadProfile(profile).then(
          (data) => {
            const item = data?.items?.[Number(iid)]
            if (item) {
              const options = shortcode.attributes.options
                ? decodeTooltipOptions(shortcode.attributes.options)
                : undefined
              if (shortcode.attributes.preview) {
                return (
                  <span className="poeplanner-wrapper">
                    {render(
                      <ItemDisplay
                        item={item}
                        options={options}
                        label={Boolean(Number(shortcode.attributes.preview))}
                      />
                    )}
                  </span>
                )
              } else if (shortcode.attributes.tooltip) {
                return render(<ItemTooltip item={item} options={options} />)
              } else {
                return render(
                  <ProfileItemNode
                    {...content}
                    item={item}
                    icon={iconOnly}
                    options={options}
                  />
                )
              }
            }
          },
          () => null
        )
      } else {
        return render(
          <ItemNode
            {...content}
            id={iid}
            icon={iconOnly}
            variant={shortcode.attributes.variant ?? undefined}
          />
        )
      }
      break
    case 'poe-passive':
    case 'poe-atlas':
      return render(
        <PassiveNode
          {...content}
          id={iid}
          atlas={false /*node.classList.contains('poe-atlas')*/}
        />
      )
    case 'poe-skill':
      return render(<SkillNode {...content} id={iid} />)
  }

  return ''
}

export function TextRenderer({ value }: { value: string }) {
  const shortcodes = parseShortcodes(value)

  if (shortcodes.length === 0) {
    return value
  }

  let resultParts = []
  let remaining = value
  shortcodes.forEach((shortcode) => {
    const shortcodeContent = <span>{getShortCodeContent(shortcode)}</span>
    const shortcodeRegex = new RegExp(
      `\\[${shortcode.type} id=${shortcode.attributes.id}.*?\\[\\/${shortcode.type}\\]`,
      'g'
    )
    const firstMatch = [...remaining.matchAll(shortcodeRegex)]?.[0]

    if (firstMatch) {
      resultParts.push(remaining.substring(0, firstMatch.index))
      resultParts.push(shortcodeContent)
      remaining = remaining.substring(
        firstMatch.index! + firstMatch['0'].length
      )
    }
  })
  resultParts.push(remaining)

  return (
    <>
      {resultParts.map((item, index) => (
        <span key={index}>{item}</span>
      ))}
    </>
  )
}

function youtubeId(href: string) {
  try {
    const url = new URL(href)
    if (
      url.hostname.match(/^(?:.*\.)?youtube\.com$/i) &&
      url.pathname === '/watch'
    ) {
      return url.searchParams.get('v')
    } else if (url.hostname.match(/^youtu\.be$/i)) {
      return url.pathname.substring(1)
    }
  } catch (e) {
    return null
  }
}

const isParent = (node: Node): node is Parent => 'children' in node
const isParagraph = (node: Node): node is Parent => node.type === 'paragraph'
const isLink = (node: Node): node is Node & { url: string } =>
  node.type === 'link'

const youtubePlugin: Plugin = () =>
  function (tree) {
    if (!isParent(tree)) return tree
    tree.children = tree.children.map((child) => {
      if (
        isParagraph(child) &&
        child.children.length === 1 &&
        isLink(child.children[0])
      ) {
        const link = child.children[0]
        const id = youtubeId(link.url)
        if (id && id.match(/^[\w_-]+$/)) {
          return {
            ...child,
            type: 'embed',
            children: [],
            id
          }
        }
      }
      return child
    })
    return tree
  }

function EmbedRenderer({ id }: { id: string }) {
  return (
    <div className="d2md-embed">
      <iframe
        width="100%"
        height="100%"
        frameBorder="0"
        src={`https://www.youtube.com/embed/${id}`}
        title="YouTube"
      />
    </div>
  )
}

const plugins = [gfm, youtubePlugin]
const renderers = {
  link: LinkRenderer,
  embed: EmbedRenderer,
  text: TextRenderer
}

export const RenderMarkdown = ({ value }: { value: string }) => (
  <ReactMarkdown plugins={plugins} renderers={renderers} children={value} />
)
